/**
 * API Timezone Handler
 * Automatically handles timezone conversion for API requests and responses
 */

import { 
  convertMultipleUTCToLocal, 
  convertMultipleLocalToUTC, 
  COMMON_TIMESTAMP_FIELDS 
} from './timezoneUtils';

/**
 * Interceptor for API responses - converts UTC timestamps to local timezone
 * @param {Object} response - Axios response object
 * @param {Array} timestampFields - Fields to convert (default: COMMON_TIMESTAMP_FIELDS)
 * @returns {Object} Response with converted timestamps
 */
export const handleApiResponse = (response, timestampFields = COMMON_TIMESTAMP_FIELDS) => {
  if (!response || !response.data) return response;
  
  try {
    // Convert timestamps in response data
    const convertedData = convertMultipleUTCToLocal(response.data, timestampFields);
    
    return {
      ...response,
      data: convertedData
    };
  } catch (error) {
    console.error('Error handling API response timezone conversion:', error);
    return response;
  }
};

/**
 * Interceptor for API requests - converts local timestamps to UTC
 * @param {Object} config - Axios request config
 * @param {Array} timestampFields - Fields to convert (default: COMMON_TIMESTAMP_FIELDS)
 * @returns {Object} Request config with converted timestamps
 */
export const handleApiRequest = (config, timestampFields = COMMON_TIMESTAMP_FIELDS) => {
  if (!config || !config.data) return config;
  
  try {
    // Convert timestamps in request data
    const convertedData = convertMultipleLocalToUTC(config.data, timestampFields);
    
    return {
      ...config,
      data: convertedData
    };
  } catch (error) {
    console.error('Error handling API request timezone conversion:', error);
    return config;
  }
};

/**
 * Enhanced axios instance with automatic timezone handling
 * @param {Object} axiosInstance - Axios instance
 * @param {Array} timestampFields - Fields to convert (default: COMMON_TIMESTAMP_FIELDS)
 * @returns {Object} Enhanced axios instance
 */
export const enhanceAxiosWithTimezone = (axiosInstance, timestampFields = COMMON_TIMESTAMP_FIELDS) => {
  // Request interceptor - convert local to UTC
  axiosInstance.interceptors.request.use(
    (config) => handleApiRequest(config, timestampFields),
    (error) => Promise.reject(error)
  );
  
  // Response interceptor - convert UTC to local
  axiosInstance.interceptors.response.use(
    (response) => handleApiResponse(response, timestampFields),
    (error) => Promise.reject(error)
  );
  
  return axiosInstance;
};

/**
 * Manual timezone conversion for specific API calls
 * @param {Object} payload - API payload
 * @param {Array} timestampFields - Fields to convert
 * @returns {Object} Payload with UTC timestamps
 */
export const preparePayloadForApi = (payload, timestampFields = COMMON_TIMESTAMP_FIELDS) => {
  return convertMultipleLocalToUTC(payload, timestampFields);
};

/**
 * Manual timezone conversion for API responses
 * @param {Object} responseData - API response data
 * @param {Array} timestampFields - Fields to convert
 * @returns {Object} Response data with local timestamps
 */
export const processApiResponse = (responseData, timestampFields = COMMON_TIMESTAMP_FIELDS) => {
  return convertMultipleUTCToLocal(responseData, timestampFields);
};